import en from './translations/en';
import es from './translations/es';

/**
 * Deep-merge `overlay` over `base`, returning a new object. Used to
 * back every non-English language with the English catalog so a key
 * missing from a translation resolves to the English text instead of
 * `undefined` (which crashes interpolating consumers app-wide).
 */
const withEnglishFallback = (base, overlay) => {
  const result = { ...base };
  Object.keys(overlay || {}).forEach(key => {
    const overlayValue = overlay[key];
    if (
      overlayValue &&
      typeof overlayValue === 'object' &&
      !Array.isArray(overlayValue) &&
      base[key] &&
      typeof base[key] === 'object'
    ) {
      result[key] = withEnglishFallback(base[key], overlayValue);
    } else {
      result[key] = overlayValue;
    }
  });
  return result;
};

export const SUPPORTED_LANGUAGES = { en, es: withEnglishFallback(en, es) };
const DEFAULT_LANGUAGE = 'en';
const LANGUAGE_STORAGE_KEY = 'appLanguage';

/**
 * Get the currently selected language from localStorage.
 * Falls back to DEFAULT_LANGUAGE if not set or invalid.
 */
export const getSelectedLanguage = () => {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return stored && SUPPORTED_LANGUAGES[stored] ? stored : DEFAULT_LANGUAGE;
};

/**
 * Set the language in localStorage.
 */
export const setSelectedLanguage = (lang) => {
  if (SUPPORTED_LANGUAGES[lang]) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  }
};

export const supportedLanguages = Object.keys(SUPPORTED_LANGUAGES);

/**
 * Validate that all translation keys match between languages.
 * Only runs in development mode.
 */
if (process.env.NODE_ENV === 'development') {
  const findMissingKeys = (source, target, sourceName, targetName, path = '') => {
    if (!source || !target) return;
    Object.keys(source).forEach(key => {
      const fullPath = path ? `${path}.${key}` : key;
      if (!(key in target)) {
        console.warn(`[i18n] Missing key in ${targetName}: "${fullPath}"`);
      } else if (typeof source[key] === 'object' && typeof target[key] === 'object') {
        findMissingKeys(source[key], target[key], sourceName, targetName, fullPath);
      }
    });
  };

  // Only validate if es has content (skip empty placeholder)
  if (Object.keys(es).length > 0) {
    findMissingKeys(en, es, 'en', 'es');
    findMissingKeys(es, en, 'es', 'en');
  }
}

const selectedLang = getSelectedLanguage();
const translations = SUPPORTED_LANGUAGES[selectedLang];

// Export with 'en' key wrapper for i18next compatibility
// i18next expects { [languageCode]: { [namespace]: ... } }
export default {
  en: translations,
};
